import React, {ReactNode} from 'react';

import {deprecatedTrackEvent} from 'analytics/Analytics';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsDashboardActions} from 'insights/analytics-dashboards/AnalyticsDashboardActions';
import * as MakeSpecRequest from 'insights/analytics-dashboards/data/MakeSpecRequest';
import ActionBarSkeleton from 'insights/analytics-dashboards/details/ActionBarSkeleton';
import {AnalyticsQueryActions, cloneAnalyticsQuery} from 'insights/analytics-queries/AnalyticsQueryActions';
import AnalyticsBlock from 'insights/analytics-queries/dashboard-block/AnalyticsBlock';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import AnalyticsTabSkeleton from 'insights/AnalyticsTabSkeleton';
import {DEFAULT_BLOCK_HEIGHT, EXPANDED_BLOCK_HEIGHT, MINIMUM_BLOCK_WIDTH} from 'insights/InsightsConstants';
import assertWellFormedLayout from 'insights/layouts/assertWellFormedLayout';
import moveBlockLayoutPosition from 'insights/layouts/moveBlockLayoutPosition';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {useWritableContext} from 'insights/WritableContext';
import DnDCanvas from 'ui-library/drag-and-drop/DnDCanvas';
import {DnDDragHandleProps} from 'ui-library/drag-and-drop/DnDDragHandle';
import {HoverPosition} from 'ui-library/drag-and-drop/hooks/useDnDCanvasDragState';
import DynamicLayout, {OneColumnDynamicLayoutRow} from 'ui-library/DynamicLayout';
import {withLoadingScreen} from 'util/Promises';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import AddBlockToDashboardButton from './AddBlockToDashboardButton';
import {useAnalyticsDashboardContext} from './AnalyticsDashboardDetailsContext';
import AnalyticsDashboardEditorHeader from './AnalyticsDashboardEditorHeader';
import AnalyticsDashboardNoBlocksContent from './AnalyticsDashboardNoBlocksContent';
import {GetAnalyticsDashboard} from './graphql/__generated/GetAnalyticsDashboard';
import RefreshDashboardButton from './refresh-dashboard/RefreshDashboardButton';
import {
  analyticsDashboardGlobalScreenState,
  DashboardEditorScreenState,
  getValidExpandedAnalyticsQueryId,
  isDashboardEditorScreenState,
  collapseBlock,
  expandBlock,
} from './view-state/ScreenState';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsQuery = AnalyticsDashboard['analyticsQueries'][number];

type UnconnectedActions = Pick<AnalyticsDashboardActions, 'updateAnalyticsDashboard'> & {
  deleteAnalyticsQuery: (analyticsQueryId: string) => void;
  openBlockEditor: (analyticsQueryId: string) => void;
  updateParameterConfigsAndValues: (
    newParameterConfigs: Array<AnalyticsParameterConfigGraphQL>,
    newParameterValues: Array<AnalyticsParameterValue>
  ) => void;
  updateParameterValues: (newValues: Array<AnalyticsParameterValue>) => void;
};
type ConnectedActions = Pick<AnalyticsQueryActions, 'cloneAnalyticsQuery'> & {
  collapseBlock: () => void;
  expandBlock: (analyticsQueryId: string) => void;
};

type Actions = ConnectedActions & UnconnectedActions;

export interface AnalyticsDashboardEditorProps {
  actions: Actions;
  analyticsDashboard: AnalyticsDashboard;
  screenState: DashboardEditorScreenState;
  parameterList: ReactNode | null;
}

/**
 * AnalyticsDashboardEditor is one of the 2 editors of AnalyticsDashboardDetails. It renders multiple blocks into
 * a grid (with dashboard header). It's the default editor, and the user can navigate to the block editor by
 * choosing a block to edit.
 */
export function AnalyticsDashboardEditor({
  actions,
  analyticsDashboard,
  screenState,
  parameterList,
}: AnalyticsDashboardEditorProps): JSX.Element {
  const writable = useWritableContext();

  async function cloneBlock(analyticsQueryId: string): Promise<void> {
    const clonedAnalyticsQuery = await withLoadingScreen(
      actions.cloneAnalyticsQuery(analyticsQueryId, {
        destinationDashboardId: analyticsDashboard.id,
      }),
      'Duplicating block...'
    );
    actions.openBlockEditor(clonedAnalyticsQuery.id);
  }

  async function moveBlocks(
    sourceAnalyticsQuery: AnalyticsQuery,
    targetAnalyticsQuery: AnalyticsQuery,
    hoverPosition?: HoverPosition
  ): Promise<void> {
    const {layoutChanged, newLayoutRows} = moveBlockLayoutPosition(
      analyticsDashboard.layoutRows,
      sourceAnalyticsQuery,
      targetAnalyticsQuery,
      hoverPosition
    );

    if (layoutChanged) {
      await withLoadingScreen(
        actions.updateAnalyticsDashboard(
          analyticsDashboard.id,
          MakeSpecRequest.onChangeLayoutRows(analyticsDashboard, newLayoutRows)
        )
      );
      deprecatedTrackEvent(INSIGHTS_ANALYTICS_ACTIONS.CHANGED_BLOCK_LAYOUT);
    }
  }

  async function updateAnalyticsQuery(
    analyticsQueryId: string,
    querySpecRequest: AnalyticsQuerySpecRequest
  ): Promise<void> {
    await withLoadingScreen(
      actions.updateAnalyticsDashboard(
        analyticsDashboard.id,
        MakeSpecRequest.onChangeAnalyticsQuery(analyticsDashboard, analyticsQueryId, querySpecRequest)
      ),
      'Updating block...'
    );
  }

  function handleToggleExpandBlock(analyticsQueryId: string, newIsExpanded: boolean): void {
    if (newIsExpanded) {
      actions.expandBlock(analyticsQueryId);
    } else {
      actions.collapseBlock();
    }
  }

  const sharedBlockProps = {
    actions: {
      cloneBlock,
      deleteBlock: actions.deleteAnalyticsQuery,
      updateAnalyticsQuery,
      updateParameterValues: actions.updateParameterValues,
      openBlockEditor: actions.openBlockEditor,
      toggleExpandBlock: handleToggleExpandBlock,
    },
    dashboardAttrs: analyticsDashboard,
    parameterConfigs: analyticsDashboard.parameterConfigs,
    parameterValues: analyticsDashboard.parameterValues,
  };

  const header = <AnalyticsDashboardEditorHeader actions={actions} analyticsDashboard={analyticsDashboard} />;

  const actionBar = (
    <ActionBarSkeleton
      leftButtons={
        <>
          <RefreshDashboardButton analyticsDashboard={analyticsDashboard} />
        </>
      }
      parameterList={parameterList}
      rightButtons={
        writable ? (
          <AddBlockToDashboardButton actions={actions} analyticsDashboard={analyticsDashboard} />
        ) : null
      }
    />
  );

  const renderBlock = (
    analyticsQuery: AnalyticsQuery,
    dragHandleProps: DnDDragHandleProps,
    index: number
  ): JSX.Element => (
    <AnalyticsBlock
      {...sharedBlockProps}
      analyticsQuery={analyticsQuery}
      index={index}
      dragHandleProps={dragHandleProps}
      isExpanded={false}
    />
  );

  let blockLayout;

  const expandedAnalyticsQueryId = getValidExpandedAnalyticsQueryId(analyticsDashboard, screenState);
  if (expandedAnalyticsQueryId != null) {
    blockLayout = (
      <DynamicLayout>
        <OneColumnDynamicLayoutRow height={EXPANDED_BLOCK_HEIGHT}>
          <AnalyticsBlock
            {...sharedBlockProps}
            analyticsQuery={analyticsDashboard.analyticsQueries.find(
              (query) => query.id === expandedAnalyticsQueryId
            )!}
            index={0}
            isExpanded={true}
          />
        </OneColumnDynamicLayoutRow>
      </DynamicLayout>
    );
  } else {
    const dndBlockGrid = analyticsDashboard.layoutRows.map((row) => row.analyticsQueries);
    blockLayout =
      dndBlockGrid.length > 0 ? (
        <DnDCanvas
          items={dndBlockGrid}
          blockHeight={DEFAULT_BLOCK_HEIGHT}
          maxColumnCount={2}
          minBlockWidth={MINIMUM_BLOCK_WIDTH}
          onMoveItem={moveBlocks}
          renderItem={renderBlock}
        />
      ) : (
        <AnalyticsDashboardNoBlocksContent actions={actions} analyticsDashboard={analyticsDashboard} />
      );

    assertWellFormedLayout(analyticsDashboard, 'while rendering layout in AnalyticsDashboardEditor');
  }

  return (
    <AnalyticsTabSkeleton
      header={
        <>
          {header}
          {actionBar}
        </>
      }
      dataTestAttrs={{component: 'AnalyticsDashboardEditor', element: 'root'}}
    >
      {blockLayout}
    </AnalyticsTabSkeleton>
  );
}

export default function ConnectedAnalyticsDashboardEditor(
  props: Omit<AnalyticsDashboardEditorProps, 'actions' | 'screenState'> & {actions: UnconnectedActions}
): JSX.Element | null {
  const {analyticsDashboardViewStateId} = useAnalyticsDashboardContext();
  const [screenState, setScreenState] = analyticsDashboardGlobalScreenState.useGlobalClientState(
    analyticsDashboardViewStateId
  );

  if (!isDashboardEditorScreenState(screenState)) {
    return null;
  }

  return (
    <AnalyticsDashboardEditor
      {...props}
      actions={{
        ...props.actions,
        collapseBlock: () => {
          setScreenState(collapseBlock(screenState));
        },
        expandBlock: (analyticsQueryId: string) => {
          setScreenState(expandBlock(screenState, analyticsQueryId));
        },
        cloneAnalyticsQuery,
      }}
      screenState={screenState}
    />
  );
}
